import React from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import AnimatedCounter from './AnimatedCounter';

const TrustSignals = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.3 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9, rotateX: -15 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      rotateX: 0,
      transition: { 
        duration: 0.8,
        ease: [0.215, 0.610, 0.355, 1.000]
      },
    },
  };

const stats = [
  {
    value: 100,
    label: 'Safety Record',
    suffix: '%',
    icon: '🛡️',
    color: 'from-midnight-navy to-sky-700' // Trusted, clean green
  },
  {
    value: '24/7',
    label: 'Global Support',
    icon: '🌍',
    color: 'from-cyan-300 to-cyan-500' // Bright aviation accent
  },
  {
    value: 10,
    label: 'Projects Completed',
    suffix: '+',
    icon: '✈️',
    color: 'from-midnight-navy to-sky-700' // Brand accent (custom color `#9FCBDC`)
  },
  {
    value: 5,
    label: 'Countries Served',
    suffix: '+',
    icon: '🗺️',
    color: 'from-cyan-300 to-cyan-500' // Cool-dark spectrum
  }
];


  return (
    <section 
      ref={ref} 
      className="py-24 relative overflow-hidden bg-gradient-to-br from-white via-cloud-beige to-white"
    >
      {/* Modern Background Elements */}
      <div className="absolute inset-0">
        <motion.div 
          className="absolute top-0 right-1/4 w-96 h-96 bg-aero-blue/5 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div 
          className="absolute bottom-0 left-1/4 w-96 h-96 bg-midnight-navy/5 rounded-full blur-3xl"
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.6, 0.3, 0.6]
          }}
          transition={{ duration: 8, repeat: Infinity, delay: 4 }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="text-center space-y-16"
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="space-y-6">
            <motion.h2 
              className="text-5xl md:text-6xl font-bold tracking-tight text-midnight-navy"
              whileHover={{ 
                scale: 1.02,
                textShadow: "0 0 30px rgba(23, 33, 74, 0.3)"
              }}
            >
              Trusted by <span className="bg-gradient-to-r from-aero-blue to-cyan-400 bg-clip-text text-transparent">Aviation Leaders</span>
            </motion.h2>
            <p className="text-xl max-w-3xl mx-auto font-light leading-relaxed text-shadow-gray">
              Founded in 2024, NZ-based with global capabilities, serving the aviation industry with precision and reliability.
            </p>
          </motion.div>

          {/* Trust Metrics with Animated Counters */}
          <motion.div
            variants={containerVariants}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                variants={itemVariants}
                className="group relative"
                whileHover={{ 
                  scale: 1.05, 
                  y: -15,
                  rotateY: 5,
                  rotateX: 5
                }}
                transition={{ duration: 0.4 }}
                style={{ transformStyle: 'preserve-3d' }}
              >
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-500 border border-gray-100 hover:bg-white/90 relative overflow-hidden">
                  {/* 3D Shiny effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 transform -skew-x-12 -translate-x-full transition-transform duration-1000 group-hover:translate-x-full group-hover:opacity-20"></div>
                  
                  {/* Icon with gradient background and 3D effect */}
                  <motion.div 
                    className={`w-20 h-20 bg-gradient-to-r ${stat.color} rounded-3xl flex items-center justify-center text-3xl mb-6 mx-auto shadow-2xl relative`}
                    whileHover={{ 
                      scale: 1.2,
                      rotateY: 15,
                      rotateX: 10,
                      boxShadow: "0 20px 40px rgba(0,0,0,0.3)"
                    }}
                    transition={{ duration: 0.3 }}
                    style={{ transformStyle: 'preserve-3d' }}
                  >
                    <span style={{ transform: 'translateZ(10px)' }}>{stat.icon}</span>
                  </motion.div>
                  
                  <div className="relative z-10">
                    <motion.div
                      className="text-5xl font-bold mb-3 transition-colors duration-300 text-midnight-navy group-hover:text-aero-blue"
                      whileHover={{ 
                        scale: 1.1,
                        textShadow: "0 0 20px rgba(159, 203, 220, 0.8)"
                      }}
                    >
                      <AnimatedCounter 
                        end={stat.value} 
                        duration={2500}
                        delay={index * 300} // Stagger the animations
                        suffix={stat.suffix || ''}
                        className="inline-block"
                      />
                    </motion.div>
                    <div className="text-shadow-gray group-hover:text-midnight-navy font-medium transition-colors duration-300">
                      {stat.label}
                    </div>
                  </div>

                  {/* 3D Depth Effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-aero-blue/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl"></div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Certifications & Credentials with 3D Cards */}
          <motion.div 
            variants={itemVariants} 
            className="bg-gradient-to-r from-midnight-navy/5 to-aero-blue/5 backdrop-blur-sm rounded-3xl p-10 border border-midnight-navy/10 relative overflow-hidden"
            whileHover={{ 
              scale: 1.02,
              rotateX: 2,
              y: -5
            }}
            style={{ transformStyle: 'preserve-3d' }}
          >
            {/* Background pattern */}
            <div className="absolute inset-0 bg-gradient-to-r from-aero-blue/5 via-transparent to-midnight-navy/5"></div>
            
            <div className="relative z-10">
              <h3 className="text-3xl font-bold mb-10 tracking-tight text-midnight-navy">
                Certifications & Compliance
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  { name: 'CAA Certified', desc: 'Licensed Aircraft Enginner' },
                  { name: 'CAA Certified', desc: 'Inspection Authoristed (IA)' },
                  { name: 'CASA B1.3', desc: 'Licensed Enginner' }
                ].map((cert, index) => (
                  <motion.div
                    key={cert.name}
                    className="flex flex-col items-center space-y-3 p-6 rounded-2xl bg-white/50 hover:bg-white/70 backdrop-blur-sm transition-all duration-500"
                    initial={{ opacity: 0, scale: 0.8, rotateY: -15 }}
                    animate={isInView ? { opacity: 1, scale: 1, rotateY: 0 } : { opacity: 0, scale: 0.8, rotateY: -15 }}
                    transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                    whileHover={{ 
                      scale: 1.05,
                      rotateY: 5,
                      y: -5
                    }}
                    style={{ transformStyle: 'preserve-3d' }}
                  >
                    <motion.div 
                      className="w-4 h-4 bg-gradient-to-r from-aero-blue to-cyan-400 rounded-full"
                      whileHover={{ scale: 1.3, rotateZ: 180 }}
                      transition={{ duration: 0.3 }}
                    />
                    <span className="font-semibold text-midnight-navy">
                      {cert.name}
                    </span>
                    <span className="text-sm text-center text-shadow-gray">
                      {cert.desc}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default TrustSignals;